<template>
  <div :class="[$style['field'], { [$style['field--full-width']]: fullWidth }]">
    <label v-if="label && !hideLabel" :for="id" :class="$style['field__label']">{{ label }}</label>
    <div :class="$style['field__control']">
      <button
        type="button"
        :class="$style['field__step']"
        :disabled="disabled || modelValue <= min"
        :aria-label="`${label ? `${label}: ` : ''}één minder`"
        :aria-controls="id"
        @click="set(modelValue - 1)">
        <Icon name="Minus" />
      </button>
      <input
        :id="id"
        :value="modelValue"
        type="number"
        inputmode="numeric"
        :min="min"
        :max="max"
        :disabled="disabled"
        :aria-label="hideLabel ? label : undefined"
        :class="$style['field__input']"
        @input="onInput"
        @change="onChange">
      <button
        type="button"
        :class="$style['field__step']"
        :disabled="disabled || (max !== undefined && modelValue >= max)"
        :aria-label="`${label ? `${label}: ` : ''}één meer`"
        :aria-controls="id"
        @click="set(modelValue + 1)">
        <Icon name="Plus" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
// A number with − and + buttons. Typing updates as you go; leaving the field puts it back within min and max.
const props = withDefaults(defineProps<{
  modelValue: number
  label?: string
  // Keeps the label for screen readers only, for compact rows
  hideLabel?: boolean
  min?: number
  max?: number
  disabled?: boolean
  fullWidth?: boolean
}>(), {
  min: 1,
  label: undefined,
  hideLabel: false,
  max: undefined,
  disabled: false,
  fullWidth: false
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const id = useId()

const clamp = (value: number) => props.max !== undefined
  ? Math.min(Math.max(value, props.min), props.max)
  : Math.max(value, props.min)

function set (value: number) {
  emit('update:modelValue', clamp(value))
}

function onInput (e: Event) {
  const raw = parseInt((e.target as HTMLInputElement).value)
  if (!isNaN(raw) && raw === clamp(raw)) { emit('update:modelValue', raw) }
}

function onChange (e: Event) {
  const input = e.target as HTMLInputElement
  const raw = parseInt(input.value)
  const value = isNaN(raw) ? props.modelValue : clamp(raw)
  input.value = String(value)
  emit('update:modelValue', value)
}
</script>

<style module>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-s);
}

.field__label {
  font-size: var(--font-size);
  font-weight: var(--font-weight-bold);
}

.field__control {
  display: inline-flex;
  align-items: stretch;
  align-self: flex-start;
  border: 1px solid var(--border-subtle);
  border-radius: 999px;
}

.field__step {
  display: grid;
  place-items: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--text-base);
  cursor: pointer;
}

.field__step:focus-visible {
  outline: 0.125rem solid var(--text-base);
  outline-offset: -0.125rem;
}

.field__step:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.field__step:hover:not(:disabled) {
  background: var(--opacity-surface);
}

.field__step svg {
  width: 1.125rem;
  height: 1.125rem;
}

.field__input {
  width: 3rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-base);
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  text-align: center;
  appearance: textfield;
}

.field__input::-webkit-outer-spin-button,
.field__input::-webkit-inner-spin-button {
  margin: 0;
  appearance: none;
}

.field__input:focus-visible {
  outline: 0.125rem solid var(--text-base);
  outline-offset: -0.125rem;
}

.field__input:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.field--full-width .field__control {
  align-self: stretch;
  justify-content: space-between;
}
</style>
